import { useQuery } from "@apollo/client"
import React, { useMemo, useState } from "react"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { gqlMatchOptional } from "~/common/gql-match"
import { useSafeMutation } from "~/common/use-safe-mutation"
import Thumbsup from "~/images/thumbsup.svg?react"
import { Button } from "~/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/ui/tooltip"
import { toast } from "~/ui/use-toast"

gql(/* GraphQL */ `
  fragment BookmarkReactions on Bookmark {
    id
    viewerHasReacted
    reactionsCount
    reactions(first: 20) {
      __typename
      nodes {
        user {
          id
          fullName
        }
      }
    }
  }
`)

const GET_BOOKMARK_REACTIONS = gql(/* GraphQL */ `
  query GetBookmarkReactions($id: ID!) {
    node(id: $id) {
      __typename
      ... on Bookmark {
        id
        ...BookmarkReactions
      }
    }
  }
`)

const ADD_BOOKMARK_REACTION = gql(/* GraphQL */ `
  mutation AddBookmarkReaction($input: AddBookmarkReactionInput!) {
    addBookmarkReaction(input: $input) {
      bookmark {
        id
        ...BookmarkReactions
      }
    }
  }
`)

const REMOVE_BOOKMARK_REACTION = gql(/* GraphQL */ `
  mutation RemoveBookmarkReaction($input: RemoveBookmarkReactionInput!) {
    removeBookmarkReaction(input: $input) {
      bookmark {
        id
        ...BookmarkReactions
      }
    }
  }
`)

type BookmarkReactionsProps = {
  bookmarkId: string
  reactionsCount: number
  viewerHasReacted: boolean
  refetch: () => Promise<any>
}

export const BookmarkReactions: React.FC<BookmarkReactionsProps> = ({
  bookmarkId,
  reactionsCount,
  viewerHasReacted,
}) => {
  const { viewer } = useViewerMaybe()
  const [isLikeLoading, setIsLikeLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [hasEverOpened, setHasEverOpened] = useState(false)

  const { data, loading } = useQuery(GET_BOOKMARK_REACTIONS, {
    variables: { id: bookmarkId },
    skip: !hasEverOpened,
  })

  const [addReaction] = useSafeMutation(ADD_BOOKMARK_REACTION, {
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  const [removeReaction] = useSafeMutation(REMOVE_BOOKMARK_REACTION, {
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  const handleReactionToggle = async () => {
    if (isLikeLoading) return

    setIsLikeLoading(true)
    try {
      if (viewerHasReacted) {
        await removeReaction({
          variables: { input: { bookmarkId } },
          refetchQueries: ["GetBookmarkReactions"],
        })
      } else {
        await addReaction({
          variables: { input: { bookmarkId } },
          refetchQueries: ["GetBookmarkReactions"],
        })
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to toggle reaction",
        variant: "destructive",
      })
    } finally {
      setIsLikeLoading(false)
    }
  }

  const nameFormatter = useMemo(
    () =>
      new Intl.ListFormat("en", {
        style: "long",
        type: "conjunction",
      }),
    []
  )

  const bookmark = gqlMatchOptional(data?.node, "Bookmark")
  const reactions = bookmark?.reactions?.nodes || []

  return (
    <TooltipProvider>
      <Tooltip
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (open) setHasEverOpened(true)
        }}
        delayDuration={0}
      >
        <TooltipTrigger asChild>
          <div>
            <Button
              variant={viewerHasReacted ? "default" : "outline"}
              size="sm"
              className={`flex items-center gap-2 rounded-full`}
              onClick={handleReactionToggle}
              disabled={isLikeLoading}
            >
              <Thumbsup />
              {reactionsCount > 0 && <span>{reactionsCount}</span>}
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-prose">
          <div>
            {loading ? (
              <span className="text-sm text-gray-500">Loading...</span>
            ) : data == null ? null : reactions.length === 0 ? (
              <span className="text-sm text-gray-500">Be the first to like this bookmark</span>
            ) : (
              <>
                {nameFormatter.format(
                  reactions.map((reaction) =>
                    reaction.user.id === viewer?.id ? "You" : reaction.user.fullName
                  )
                )}
                {reactionsCount > reactions.length ? (
                  <span className="text-gray-500">
                    {" "}
                    and {reactionsCount - reactions.length} more liked this bookmark
                  </span>
                ) : (
                  <span> liked this bookmark</span>
                )}
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
